import { useState } from 'react';
import { products } from '../../data/content';
import BrandedImage from '../../components/BrandedImage';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useCart } from '../../context/CartContext';
import './MorePage.css';

function MorePage() {
  const { addToCart } = useCart();
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleAddToCart = (product) => {
    if (product.price > 0) setSelectedProduct(product);
  };

  const confirmAddToCart = (quantity) => {
    if (selectedProduct) {
      addToCart({ ...selectedProduct, quantity });
      setSelectedProduct(null);
    }
  };

  return (
    <div className="more-page-wrapper">

      {selectedProduct && (
        <ConfirmationModal
          product={selectedProduct}
          onConfirm={confirmAddToCart}
          onCancel={() => setSelectedProduct(null)}
        />
      )}

      {/* ══ HERO ══ */}
      <section className="more-hero">
        <span className="more-hero-kicker">Our Products</span>
        <h1>Farm-Fresh <em>Corn</em> Packs for Every Need</h1>
        <p className="more-hero-desc">
          Ready-to-eat, preservative-free sweet corn — in sizes that suit everyone from individual
          snackers to HoReCa bulk buyers. Straight from Badulla highlands to your table.
        </p>
      </section>

      {/* ══ PRODUCTS ══
          Desktop: CSS grid (4 → 3 → 2 cols)
          Mobile:  CSS forces single column — same look as HomePage */}
      <section className="more-products-section">
        <span className="more-section-label">What We Offer</span>
        <h2 className="more-section-title">Corn Bliss Packages</h2>

        <div className="more-product-grid">
          {products.map((product) => (
            <div className="more-product-card" key={product.title}>

              <div className="more-product-img-wrap">
                {product.badge && (
                  <div
                    className="product-card-badge"
                    style={{
                      background: `linear-gradient(135deg, ${product.badgeColor} 0%, ${product.badgeColor}cc 100%)`,
                    }}
                  >
                    {product.badge}
                  </div>
                )}

                <BrandedImage
                  src={product.image}
                  alt={product.title}
                  imageClassName="card-image"
                />
              </div>

              <div className="more-product-info">
                <h3 className="more-product-title">{product.title}</h3>
                {product.bestFor && (
                  <p className="more-product-description">{product.bestFor}</p>
                )}
              </div>

              <div className="more-product-actions">
                <button
                  className="more-add-btn"
                  onClick={() => handleAddToCart(product)}
                  disabled={product.price === 0}
                >
                  {product.price === 0 ? 'Coming Soon' : '+ Add to Cart'}
                </button>
              </div>

            </div>
          ))}
        </div>
      </section>

      {/* ══ TESTIMONIALS ══ */}
      <section className="more-testimonials-section">
        <span className="more-testimonials-label">Happy Customers</span>
        <h2 className="more-testimonials-title">What Our Customers Say</h2>

        {/* Auto-sliding review rows */}
        <div className="more-reviews-wall">
          {/* Row 1 — slides left */}
          <div className="more-review-row-wrap">
            <div className="more-review-row more-row-left">
              {[
                { name: 'Saymini Perera', text: 'So delicious and fresh. Going on my 2nd order just within 24 hours. Continue keeping this quality!', stars: 5, source: 'Facebook' },
                { name: 'Krishanthi Arachchige', text: 'Highly recommended. Very reliable service and very generous. Wish you all the success!', stars: 5, source: 'Facebook' },
                { name: 'Tharaka Herath', text: "Ordered three times now and it's always the best! 🌽 Ordered for Katina Ceremony and everyone loved it. Highly recommended!", stars: 5, source: 'Facebook' },
                { name: 'Koshila Liyanaarachchi', text: "Extremely satisfied. Packaging was neat, freshness and rich taste immediately noticeable. Absolutely worth the price. Will definitely order more!", stars: 5, source: 'Facebook' },
                { name: 'Kavindi Watareka', text: 'Corn kernels are super tasty, creamy and easy to make. Super convenient and clean. Delivered on time. Recommend specially for kids!', stars: 5, source: 'Facebook' },
                { name: 'Anukshini Fernando', text: 'My kids love corn bliss corn. I frequently order because they love to take it to school. Highly recommend!', stars: 5, source: 'Facebook' },
                { name: 'Rehana Azeez', text: 'Really good product, clean and well packed. Can be cooked in few minutes. We love it with butter. ❤️', stars: 5, source: 'Facebook' },
                { name: 'Dhanushka Chandimail', text: 'A really great place. Incredible discounts and wholesale pricing even in small amounts. Good customer service.', stars: 5, source: 'Google' },
                { name: 'Sandani Kumararunga', text: 'Amazing discounts on all items. Best place to buy all the domestic products at lower prices.', stars: 5, source: 'Google' },
              ].concat([
                { name: 'Saymini Perera', text: 'So delicious and fresh. Going on my 2nd order just within 24 hours. Continue keeping this quality!', stars: 5, source: 'Facebook' },
                { name: 'Krishanthi Arachchige', text: 'Highly recommended. Very reliable service and very generous. Wish you all the success!', stars: 5, source: 'Facebook' },
                { name: 'Tharaka Herath', text: "Ordered three times now and it's always the best! 🌽 Everyone loved it. Highly recommended!", stars: 5, source: 'Facebook' },
              ]).map((r, i) => (
                <div key={i} className="more-review-card">
                  <div className="more-review-top">
                    <div className="more-review-avatar">{r.name.charAt(0)}</div>
                    <div className="more-review-meta">
                      <span className="more-review-name">{r.name}</span>
                      <div className="more-review-stars">{'★'.repeat(r.stars)}</div>
                    </div>
                    <span className={`more-review-source more-review-source-${r.source.toLowerCase()}`}>
                      {r.source === 'Google' ? (
                        <svg width="11" height="11" viewBox="0 0 48 48" fill="none">
                          <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 6.1 1.1 8.4 3L39.8 4.4C35.6 1.5 30.6 0 24 0 12.4 0 3 9.4 3 21s9.4 21 21 21c13.3 0 21-7.5 21-21 0-1.3-.2-2.7-.5-4z" fill="#4285F4"/>
                        </svg>
                      ) : (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="#1877F2">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                        </svg>
                      )}
                      {r.source}
                    </span>
                  </div>
                  <p className="more-review-text">"{r.text}"</p>
                </div>
              ))}
            </div>
          </div>

          {/* Row 2 — slides right */}
          <div className="more-review-row-wrap">
            <div className="more-review-row more-row-right">
              {[
                { name: 'Udayangani Mnh', text: 'Excellent product ❤️ Super fresh food. Organic products.', stars: 5, source: 'Facebook' },
                { name: 'Sandamal Perera', text: 'Easy to prepare. Kids love this. Local produce. Thank youuuu 🌽❤️', stars: 5, source: 'Facebook' },
                { name: 'Nilanga Ruwanpura', text: "I've bought this three times already — it's amazing! My daughter loves it.", stars: 5, source: 'Facebook' },
                { name: 'Bashini Gamage', text: '100% recommend for kids as well. Best quality product.', stars: 5, source: 'Facebook' },
                { name: 'Poornima Lakshani', text: 'ගොඩක් රසයි මේ corn ඇට. Highly Recommended Corn Bliss ❤️', stars: 5, source: 'Facebook' },
                { name: 'Sajana Wijesinghe', text: 'This sweet corn is really good, the service is also the best ❤️👍 Best value for money!', stars: 5, source: 'Facebook' },
                { name: 'Thiwanka Abeysinghe', text: 'Price is very reasonable. Extremely satisfied with quality. Staff are very friendly!', stars: 5, source: 'Facebook' },
                { name: 'Pubudu Lakshitha', text: 'Many things have wholesale prices. A vast range of products marketed by trusted names.', stars: 5, source: 'Google' },
                { name: 'Venushi Anuththara', text: 'Corn was in really good quality and fresh 🌽 Highly recommend 🤍', stars: 5, source: 'Facebook' },
              ].concat([
                { name: 'Udayangani Mnh', text: 'Excellent product ❤️ Super fresh food. Organic products.', stars: 5, source: 'Facebook' },
                { name: 'Sandamal Perera', text: 'Easy to prepare. Kids love this. Local produce. Thank youuuu 🌽❤️', stars: 5, source: 'Facebook' },
                { name: 'Nilanga Ruwanpura', text: "I've bought this three times already — it's amazing! My daughter loves it.", stars: 5, source: 'Facebook' },
              ]).map((r, i) => (
                <div key={i} className="more-review-card">
                  <div className="more-review-top">
                    <div className="more-review-avatar">{r.name.charAt(0)}</div>
                    <div className="more-review-meta">
                      <span className="more-review-name">{r.name}</span>
                      <div className="more-review-stars">{'★'.repeat(r.stars)}</div>
                    </div>
                    <span className={`more-review-source more-review-source-${r.source.toLowerCase()}`}>
                      {r.source === 'Google' ? (
                        <svg width="11" height="11" viewBox="0 0 48 48" fill="none">
                          <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 6.1 1.1 8.4 3L39.8 4.4C35.6 1.5 30.6 0 24 0 12.4 0 3 9.4 3 21s9.4 21 21 21c13.3 0 21-7.5 21-21 0-1.3-.2-2.7-.5-4z" fill="#4285F4"/>
                        </svg>
                      ) : (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="#1877F2">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                        </svg>
                      )}
                      {r.source}
                    </span>
                  </div>
                  <p className="more-review-text">"{r.text}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default MorePage;